// src/helpers/foodFilterBuilder.js
// Build food filter object from user meal preferences

/**
 * Build a food filter from user profile meal preferences
 * @param {Object} profile - User profile object with mealPref_* fields
 * @returns {Object} Filter object with required/excluded/avoid/prioritize tags
 */
export function buildFoodFilterFromProfile(profile) {
    if (!profile) {
        return {
            requiredTags: [],
            excludedTags: [],
            avoidTags: [],
            prioritizeTags: [],
            forbiddenKeywords: [],
            dietStyle: 'none',
            hasPreferences: false,
        };
    }

    const result = {
        requiredTags: buildRequiredTags(profile),
        excludedTags: buildExcludedTags(profile),
        avoidTags: buildAvoidTags(profile),
        prioritizeTags: buildPriorizeTags(profile),
        forbiddenKeywords: buildForbiddenKeywords(profile),
        dietStyle: profile.mealPref_dietStyle || 'none',
        hasPreferences: hasAnyMealPreferences(profile),
    };

    return result;
}

/**
 * Build required tags based on diet style
 * (Currently empty – we rely on excludedTags + keywords)
 */
function buildRequiredTags(profile) {
    const required = [];

    // Diet style does not add required tags - it adds excluded tags instead
    // This keeps the logic simpler

    return required;
}

/**
 * Build excluded tags from diet style, allergies, and religious restrictions
 * Uses CATEGORY tags (cat:) because foods in database use category-level tagging
 */
function buildExcludedTags(profile) {
    const excluded = [];

    // Diet style exclusions - using CATEGORY tags that exist in database
    const dietStyle = profile.mealPref_dietStyle;
    if (dietStyle === 'vegan') {
        excluded.push(
            'cat:meat',
            'cat:poultry',
            'cat:fish_seafood',
            'cat:organ_meat',
            'cat:eggs',
            'cat:dairy'
        );
    } else if (dietStyle === 'vegetarian') {
        excluded.push('cat:meat', 'cat:poultry', 'cat:organ_meat');
        // Strict vegetarian also excludes fish/seafood
        excluded.push('cat:fish_seafood');
    } else if (dietStyle === 'pescatarian') {
        excluded.push('cat:meat', 'cat:poultry', 'cat:organ_meat');
    } else if (dietStyle === 'noRedMeat') {
        // Red meat is in cat:meat category - we exclude the whole category
        excluded.push('cat:meat');
    }

    // Religious/ethical exclusions - using CATEGORY tags
    const religious = profile.mealPref_religious || [];
    if (religious.includes('noAlcohol')) {
        excluded.push('cat:alcohol');
    }
    if (religious.includes('halalFriendly')) {
        excluded.push('cat:meat', 'cat:alcohol');
    }
    if (religious.includes('kosherStyle')) {
        excluded.push('cat:meat', 'cat:fish_seafood'); // shellfish are in seafood
    }

    // Allergy exclusions - category-based
    const allergies = profile.mealPref_allergies || [];
    if (allergies.includes('dairyFree')) {
        excluded.push('cat:dairy');
    }
    if (allergies.includes('nutFree')) {
        excluded.push('cat:nuts_seeds');
    }
    if (allergies.includes('eggFree')) {
        excluded.push('cat:eggs');
    }
    if (allergies.includes('shellfishFree') || allergies.includes('fishFree')) {
        excluded.push('cat:fish_seafood');
    }

    return excluded;
}

/**
 * Build avoid tags from dislikes (soft filter)
 */
function buildAvoidTags(profile) {
    const avoid = [];
    const dislikes = profile.mealPref_dislikes || [];

    if (dislikes.includes('seafoodFlavor')) {
        avoid.push('cat:fish_seafood');
    }
    if (dislikes.includes('mushrooms')) {
        avoid.push('cat:mushroom');
    }

    return avoid;
}

/**
 * Build forbidden keywords from diet style and religious restrictions
 * This is a fallback for when category tags are not specific enough (e.g. prepared dishes)
 */
function buildForbiddenKeywords(profile) {
    const keywords = [];

    const dietStyle = profile.mealPref_dietStyle;
    const religious = profile.mealPref_religious || [];

    // Meat keywords for vegetarians and specific religious restrictions
    if (
        dietStyle === 'vegetarian' ||
        dietStyle === 'vegan' ||
        religious.includes('noPork') ||
        religious.includes('noBeef')
    ) {
        // Pork-specific keywords
        if (
            dietStyle === 'vegetarian' ||
            dietStyle === 'vegan' ||
            religious.includes('noPork')
        ) {
            keywords.push(
                'pork',
                'bacon',
                'ham',
                'prosciutto',
                'pancetta',
                'chorizo'
            );
        }

        // Beef-specific keywords
        if (
            dietStyle === 'vegetarian' ||
            dietStyle === 'vegan' ||
            religious.includes('noBeef')
        ) {
            keywords.push('beef', 'veal', 'steak');
        }

        // General meat keywords for vegetarians/vegans
        if (dietStyle === 'vegetarian' || dietStyle === 'vegan') {
            keywords.push(
                'meat',
                'chicken',
                'turkey',
                'lamb',
                'sausage',
                'mince',
                'meatball',
                'poultry',
                'duck',
                'salami'
            );
        }
    }

    // Alcohol keywords
    if (religious.includes('noAlcohol')) {
        keywords.push(
            'wine',
            'beer',
            'vodka',
            'rum',
            'whisky',
            'whiskey',
            'gin',
            'brandy',
            'liqueur',
            'alcohol',
            'spirit'
        );
    }

    return keywords;
}

/**
 * Build prioritize tags from convenience preferences
 */
function buildPriorizeTags(profile) {
    const prioritize = [];
    const convenience = profile.mealPref_convenience || [];

    if (convenience.includes('quickMeals')) prioritize.push('quick');
    if (convenience.includes('onePot')) prioritize.push('onePot');
    if (convenience.includes('batchCooking')) prioritize.push('batchFriendly');
    if (convenience.includes('minimalEquipment'))
        prioritize.push('minimalEquipment');

    return prioritize;
}

/**
 * Check if user has any meal preferences set
 */
function hasAnyMealPreferences(profile) {
    return !!(
        (profile.mealPref_dietStyle && profile.mealPref_dietStyle !== 'none') ||
        (profile.mealPref_allergies && profile.mealPref_allergies.length > 0) ||
        (profile.mealPref_religious && profile.mealPref_religious.length > 0) ||
        (profile.mealPref_dislikes && profile.mealPref_dislikes.length > 0) ||
        (profile.mealPref_convenience && profile.mealPref_convenience.length > 0) ||
        profile.mealPref_otherAllergy ||
        profile.mealPref_otherDislikes
    );
}

/**
 * Get human-readable diet style label
 */
export function getDietStyleLabel(dietStyle) {
    const labels = {
        none: 'No specific diet',
        vegan: 'Vegan',
        vegetarian: 'Vegetarian',
        pescatarian: 'Pescatarian',
        noRedMeat: 'No red meat',
    };
    return labels[dietStyle] || dietStyle;
}
